import {formatShortDate} from 'helpers/dates';

import {ColumnSpec, TableProjection} from './aiQueryColumns';

export type PersonRefRow = {
  user_id?: string;
  ldap?: string | null;
  name?: string | null;
  email?: string | null;
};

export type WorkstreamRefRow = {
  workstream_id?: number;
  name?: string | null;
};

export type TeamRefRow = {
  team_id?: string;
  name?: string | null;
  slug?: string | null;
};

/**
 * Coerce a dynamic result value to a trimmed string. AI query results aren't
 * guaranteed to match their declared types at runtime (a `name` field can come
 * back as a number, etc.), so we never assume `.trim()` exists on the raw value.
 */
export const toTrimmedString = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  return String(value).trim();
};

export const readField = (row: Record<string, unknown>, field: string): unknown => {
  // Prefer a literal key match: combine_results output is flattened, so a dotted
  // name like `workstream.workstream_id` is a real top-level key, not a path.
  if (Object.prototype.hasOwnProperty.call(row, field)) {
    return row[field];
  }
  if (!field.includes('.')) {
    return row[field];
  }
  let current: unknown = row;
  for (const part of field.split('.')) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

const formatDate = (value: unknown): string => {
  if (value == null || value === '') {
    return '';
  }
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.slice(0, 10);
  }
  return str;
};

const formatDateTime = (value: unknown): string => {
  if (value == null || value === '') {
    return '';
  }
  const str = String(value);
  try {
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch {
    // fall through
  }
  return str;
};

const formatPersonRef = (ref: PersonRefRow | null | undefined): string => {
  if (!ref) {
    return '';
  }
  const name = toTrimmedString(ref.name);
  const ldap = toTrimmedString(ref.ldap);
  if (name && ldap) {
    return `${name} (${ldap})`;
  }
  return name || ldap || toTrimmedString(ref.email) || toTrimmedString(ref.user_id);
};

const formatWorkstreamRef = (ref: WorkstreamRefRow | null | undefined): string => {
  if (!ref) {
    return '';
  }
  const name = toTrimmedString(ref.name);
  if (name) {
    return name;
  }
  if (ref.workstream_id != null) {
    return `#${String(ref.workstream_id)}`;
  }
  return '';
};

const formatTeamRef = (ref: TeamRefRow | null | undefined): string => {
  if (!ref) {
    return '';
  }
  const name = toTrimmedString(ref.name);
  if (name) {
    return name;
  }
  return toTrimmedString(ref.team_id);
};

export type CellTextOptions = {
  /** Humanize dates to the app's short format for on-screen display; CSV export keeps ISO. */
  humanizeDates?: boolean;
};

/** Flatten a row cell to plain text (CSV export by default; display with humanizeDates). */
export const cellValueToText = (
  row: Record<string, unknown>,
  column: ColumnSpec,
  options?: CellTextOptions
): string => {
  switch (column.kind) {
    case 'text': {
      const value = readField(row, column.field);
      if (value == null || value === '') {
        return '';
      }
      if (value === 'deliverable') {
        return 'milestone';
      }
      if (value === 'Deliverables') {
        return 'Milestones';
      }
      return String(value);
    }
    case 'date': {
      const value = readField(row, column.field);
      return options?.humanizeDates ? formatShortDate(value as string) : formatDate(value);
    }
    case 'datetime':
      return formatDateTime(readField(row, column.field));
    case 'boolean': {
      const value = readField(row, column.field);
      return value ? 'Yes' : 'No';
    }
    case 'status': {
      const status = readField(row, column.field);
      return status == null || status === '' ? '' : String(status);
    }
    case 'person':
      return formatPersonRef({
        name: readField(row, column.nameField) as string | null | undefined,
        ldap: column.ldapField
          ? (readField(row, column.ldapField) as string | null | undefined)
          : undefined,
      });
    case 'personRef':
      return formatPersonRef(readField(row, column.field) as PersonRefRow);
    case 'personRow':
      return formatPersonRef({
        user_id: row.user_id as string | undefined,
        ldap: row.ldap as string | null | undefined,
        name: row.name as string | null | undefined,
        email: row.email as string | null | undefined,
      });
    case 'personRefs': {
      const raw = readField(row, column.field);
      if (!Array.isArray(raw) || raw.length === 0) {
        return '';
      }
      return (raw as PersonRefRow[]).map((person) => formatPersonRef(person)).join(', ');
    }
    case 'workstreamRef':
      return formatWorkstreamRef(readField(row, column.field) as WorkstreamRefRow);
    case 'workstream': {
      const id = readField(row, column.idField);
      const name = readField(row, column.nameField);
      return formatWorkstreamRef({
        workstream_id: id as number | undefined,
        name: name as string | null | undefined,
      });
    }
    case 'teamRef':
      return formatTeamRef(readField(row, column.field) as TeamRefRow);
    case 'team': {
      const id = readField(row, column.idField);
      const name = readField(row, column.nameField);
      return formatTeamRef({
        team_id: id as string | undefined,
        name: name as string | null | undefined,
        slug: column.slugField
          ? (readField(row, column.slugField) as string | null | undefined)
          : undefined,
      });
    }
    case 'service':
    case 'component': {
      const name = toTrimmedString(readField(row, column.nameField));
      const id = readField(row, column.idField);
      return name || (id != null ? `#${String(id)}` : '');
    }
    default:
      return '';
  }
};

/** Prefix formula-like values to reduce CSV injection risk in spreadsheet apps. */
const guardCsvInjection = (value: string): string => {
  if (/^[=+\-@]/.test(value)) {
    return `'${value}`;
  }
  return value;
};

const escapeCsvField = (value: string): string => {
  const guarded = guardCsvInjection(value);
  if (/[",\n\r]/.test(guarded)) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
};

/** Build a CSV string from plain header + row strings (e.g. parsed answer tables). */
export const buildRawCsv = (headers: string[], rows: string[][]): string => {
  const headerLine = headers.map(escapeCsvField).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvField).join(','));
  return [headerLine, ...dataLines].join('\n');
};

/** Build a CSV string from result rows using the same column projection as the table. */
export const buildCsv = (
  items: Array<Record<string, unknown>>,
  projection: TableProjection
): string => {
  const headers = projection.columns.map((col) => col.header);
  const headerLine = headers.map(escapeCsvField).join(',');
  const dataLines = items.map((row) =>
    projection.columns.map((col) => escapeCsvField(cellValueToText(row, col))).join(',')
  );
  return [headerLine, ...dataLines].join('\n');
};

/** Human-readable label shown on the CSV download chip (e.g. ``Workstreams.csv``). */
export const csvDisplayFilename = (projection: TableProjection): string => {
  const base = (projection.title ?? 'Results').trim();
  const cleaned = base.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
  return `${cleaned || 'Results'}.csv`;
};

/** Suggested download filename from projection title + timestamp. */
export const csvFilename = (projection: TableProjection): string => {
  const base = (projection.title ?? 'results')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `${base || 'results'}-${stamp}.csv`;
};

/** Trigger a browser download of a CSV string. */
export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
